import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { createContext, useContext, useEffect, useState } from "react";
import { cn } from "@/lib/utils";

/**
 * A single step in the contextual breadcrumb trail shown in the dashboard
 * topbar. Earlier crumbs link back up the hierarchy; the last one is the
 * current page and renders as plain bold text.
 */
interface Crumb {
  label: string;
  to?: string;
  params?: Record<string, string>;
  search?: Record<string, string>;
}

// Two separate contexts so the page-side setter is referentially stable
// (it never changes identity) while only the topbar re-renders when the
// items change. A combined context would re-fire every page's effect on
// each update.
const ItemsContext = createContext<Crumb[]>([]);
const SetItemsContext = createContext<(items: Crumb[]) => void>(() => {});

export function BreadcrumbsProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<Crumb[]>([]);
  return (
    <SetItemsContext.Provider value={setItems}>
      <ItemsContext.Provider value={items}>{children}</ItemsContext.Provider>
    </SetItemsContext.Provider>
  );
}

export function useBreadcrumbItems(): Crumb[] {
  return useContext(ItemsContext);
}

/**
 * Declare the breadcrumb trail for the current page. Set on mount, cleared on
 * unmount. The serialized key keeps the effect from re-running on every render
 * while still updating when a crumb (e.g. a record name) actually changes.
 */
export function useSetBreadcrumbs(items: Crumb[]): void {
  const setItems = useContext(SetItemsContext);
  const key = JSON.stringify(items);
  useEffect(() => {
    setItems(JSON.parse(key) as Crumb[]);
    return () => setItems([]);
  }, [setItems, key]);
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  if (items.length === 0) {
    return <span className="text-sm text-muted-foreground">Dashboard</span>;
  }

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-sm">
      {items.map((crumb, index) => {
        const isLast = index === items.length - 1;
        return (
          <span key={`${crumb.label}-${crumb.to ?? ""}`} className="flex items-center gap-1.5">
            {index > 0 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />}
            {isLast || !crumb.to ? (
              <span
                className={cn(isLast ? "font-semibold text-foreground" : "text-muted-foreground")}
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.to}
                params={crumb.params}
                search={crumb.search}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                {crumb.label}
              </Link>
            )}
          </span>
        );
      })}
    </nav>
  );
}
